import { useRichTextEditorContext } from '../hooks/useRichTextEditorContext';
import LinkOutline from '@simplywallst/ui-core/icons/LinkOutline';
import ResetSolid from '@simplywallst/ui-core/icons/ResetSolid';
import PencilOutline from '@simplywallst/ui-core/icons/PencilOutline';
import OpenLinkOutline from '@simplywallst/ui-core/icons/OpenLinkOutline';
import UnlinkOutline from '@simplywallst/ui-core/icons/UnlinkOutline';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';
import { Button, cn } from '@simplywallst/ui-core/next';
import { useEffect, useId, useState } from 'react';
import { isUrlValid } from '../utils';

export const InlineToolbarActiveLink = () => {
  const { editor } = useRichTextEditorContext();

  const { href } = editor?.getAttributes('link') ?? {};
  const hrefAttribute = href ?? '';
  const [isEditActive, setIsEditActive] = useState(!href);
  const [immediateHref, setImmediateHref] = useState<string>(hrefAttribute);
  const prefixedImmediateHref =
    !immediateHref || /^https?:\/\//.test(immediateHref)
      ? immediateHref
      : `https://${immediateHref}`;

  const isLinkActive = editor?.isActive('link');

  useEffect(() => {
    setImmediateHref(hrefAttribute);
  }, [hrefAttribute]);

  useEffect(() => {
    if (!isLinkActive) {
      setIsEditActive(false);
    }
    if (isLinkActive && !hrefAttribute) {
      setIsEditActive(true);
    }
  }, [isLinkActive, hrefAttribute]);

  const updateHref = () => {
    if (hrefAttribute === immediateHref || !editor) return;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .updateAttributes('link', {
        href: prefixedImmediateHref,
      })
      .setMeta('preventAutolink', true)
      .run();

    setImmediateHref(prefixedImmediateHref);
  };

  const isDirty = immediateHref !== hrefAttribute;
  const isValid = isUrlValid(prefixedImmediateHref);
  const inputId = useId();

  if (isEditActive) {
    return (
      <div
        className={cn(
          'border grid grid-cols-[max-content_minmax(100px,200px)_max-content_max-content] items-center justify-center gap-x0_5 rounded-x1 transition',
          !isValid && immediateHref ? 'border-bad' : 'border-transparent'
        )}
      >
        <LinkOutline className="h-x5 fill-softer" />
        <label htmlFor={inputId} className="sr-only">
          Link URL
        </label>
        <input
          id={inputId}
          aria-invalid={!isValid}
          type="text"
          className={cn(
            'bg-transparent px-0 py-x1 text-xs text-solid',
            !isValid && immediateHref && 'text-bad'
          )}
          value={immediateHref ?? ''}
          onChange={(e) => setImmediateHref(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateHref();
              editor?.chain().focus().selectTextblockEnd().run();
              setIsEditActive(false);
            }
          }}
          placeholder="Enter a link URL"
          aria-required
        />
        <Button
          variant={isDirty ? 'primary' : 'subtle'}
          size="medium"
          className="px-x1"
          isDisabled={!isValid}
          onClick={() => {
            updateHref();
            setIsEditActive(false);
          }}
          aria-label="Confirm link"
        >
          <TickSolid />
        </Button>
        <Button
          variant="subtle"
          size="medium"
          className="px-x1"
          onClick={() => {
            setImmediateHref(hrefAttribute);
            setIsEditActive(false);
          }}
          aria-label="Cancel changes"
        >
          <ResetSolid />
        </Button>
      </div>
    );
  }

  return (
    <div className="grid auto-cols-max grid-flow-col items-center justify-center gap-x0_5">
      <Button
        variant="subtle"
        size="medium"
        onClick={() => {
          window.open(href, '_blank');
        }}
      >
        <OpenLinkOutline /> Open<span className="sr-only">link</span>
      </Button>
      <Button
        variant="subtle"
        size="medium"
        onClick={() => {
          setIsEditActive(true);
        }}
      >
        <PencilOutline /> Edit<span className="sr-only">link</span>
      </Button>
      <Button
        variant="subtle"
        size="medium"
        onClick={() => {
          editor?.commands.unsetLink();
        }}
      >
        <UnlinkOutline /> Unlink<span className="sr-only">link</span>
      </Button>
    </div>
  );
};
